import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '@api-new/authservice';
import { HTTP_AP_AdvisorUser, UserApiService } from '@api-new/userservice';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { getCompanyById } from '@platform/company/store/company.actions';
import { ErrorModel } from '@shared-lib/models/error.model';
import { ToastActionEnum, ToastService, ToastSeverityEnum } from '@shared-lib/services/toast.service';
import { TOAST_MESSAGE } from '@shared/constants/toast-messages';
import { getLenders } from '@shared/store/lenders/lenders.action';
import { from, of } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ANIMATION_DELAY } from '../../constants/constants';
import {
  activateUserSuccess,
  getUserById,
  getUserFailure,
  getUserSuccess,
  resetUserPassword,
  resetUserPasswordFailure,
  resetUserPasswordSuccess,
  sendResetPasswordLink,
  sendResetPasswordLinkFailure,
  sendResetPasswordLinkSuccess,
} from './user.action';

@Injectable()
export class UserEffect {
  private readonly actions$ = inject(Actions);
  private readonly authApiService = inject(AuthApiService);
  private readonly userApiService = inject(UserApiService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  getUserById = createEffect(() =>
    this.actions$.pipe(
      ofType(getUserById),
      switchMap(({ id }) => {
        return this.userApiService.HTTP_AP_GetAdvisorUser({ advisorUserId: id }).pipe(
          map((user: HTTP_AP_AdvisorUser) => getUserSuccess({ user })),
          catchError((error) => of(getUserFailure({ error }))),
        );
      }),
    ),
  );

  handleGetUserSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(getUserSuccess),
      switchMap(({ user }) => of(getCompanyById({ id: user.companyId }), getLenders())),
    ),
  );

  activateAccountRedirect = createEffect(
    () =>
      this.actions$.pipe(
        ofType(activateUserSuccess),
        delay(ANIMATION_DELAY),
        tap(() => this.router.navigate([``])),
      ),
    { dispatch: false },
  );

  sendPasswordReset = createEffect(() =>
    this.actions$.pipe(
      ofType(sendResetPasswordLink),
      switchMap(({ email }) =>
        this.authApiService.HTTP_AP_ProvideAdvisorUserResetPasswordCode({ email }).pipe(
          mergeMap(() => {
            this.toastService.showToast(ToastSeverityEnum.Success, TOAST_MESSAGE.SUCCESS[ToastActionEnum.ActionEmail]('Recovery'));
            return [sendResetPasswordLinkSuccess()];
          }),
          catchError((error: ErrorModel) => {
            this.toastService.showToast(ToastSeverityEnum.Error, TOAST_MESSAGE.ERROR[ToastActionEnum.ActionEmail]('Recovery'));
            return from([sendResetPasswordLinkFailure({ error })]);
          }),
        ),
      ),
    ),
  );

  adviserPasswordReset = createEffect(() => {
    return this.actions$.pipe(
      ofType(resetUserPassword),
      switchMap(({ code, password }) =>
        this.authApiService.HTTP_AP_ResetPassword_WithCode({ code, password }).pipe(
          map(() => {
            this.toastService.showToast(ToastSeverityEnum.Success, TOAST_MESSAGE.SUCCESS[ToastActionEnum.ActionUpdate]('Password'));
            return resetUserPasswordSuccess();
          }),
          catchError((error: ErrorModel) => {
            if (!error.error?.customErrorMessage) {
              this.toastService.showToast(ToastSeverityEnum.Error, TOAST_MESSAGE.ERROR[ToastActionEnum.ActionUpdate]('Password'));
            }
            return of(resetUserPasswordFailure({ error }));
          }),
        ),
      ),
    );
  });
}
